import { ChangeDetectorRef, Component, DestroyRef, effect, OnDestroy, OnInit, signal, computed, inject } from "@angular/core";
import { TranslocoService, TranslocoPipe } from "@jsverse/transloco";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BasemapService } from "../../basemap/basemap.service";
import { LayerService } from "../layer.service";
import { InterfaceStatus } from "src/app/shared/contract/Enum/Status";
import { LayerGroupInfo, LayerGroupInfoFilter, LayerInfo, LayerOverviewResponse } from "src/app/shared/contract/Layer/LayerOverview";
import { InfoText } from "../../../shared/contract/InfoText/InfoText";
import { NotificationService } from "@services/notification.service";
import { PopupService } from "../../popup/popup.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MapLayer } from "src/app/shared/contract/Enum/MapLayer";
import { MapContainerService } from "src/app/shared/services/map-container.service";
import { LayerApiService } from "../legend/layer-api.service";
import { MatDialogRef } from "@angular/material/dialog";
import { filter, tap } from "rxjs/operators";
import { cloneDeep } from "lodash-es";
import { OptionService } from "../../option/option.service";
import { MapLayerService } from "src/app/shared/services/map-layer.service";
import { LayerHeaderComponent } from "../layer-header/layer-header.component";
import { ExpansionPanelSkeletonComponent } from "../../skeleton-presets/expansion-panel-skeleton/expansion-panel-skeleton.component";
import { ListSkeletonComponent } from "../../skeleton-presets/list-skeleton/list-skeleton.component";
import { LayerGroupComponent } from "../layer-group/layer-group.component";
import { NavigationStart, Router } from "@angular/router";
import { FilterForm } from "../models/filter-form.interface";
import { FilterViewModel } from "../models/filter-view-model.interface";
import { LayerFormType } from "../models/layer-form-type.interface";
import { LayerGroupForm } from "../models/layer-group-form.interface";
import { LayerGroupViewModel } from "../models/layer-group-view-model.interface";
import { LayerInfoForm } from "../models/layer-info-form.interface";
import { LayerInfoViewModel } from "../models/layer-info-view-model.interface";
import { InfoTextService } from "@services/info-text.service";

@Component({
  selector: "app-layer",
  templateUrl: "./layer.component.html",
  styleUrls: ["./layer.component.scss"],
  imports: [
    LayerHeaderComponent,
    ExpansionPanelSkeletonComponent,
    ListSkeletonComponent,
    FormsModule,
    ReactiveFormsModule,
    LayerGroupComponent,
    TranslocoPipe
  ]
})
export class LayerComponent implements OnInit, OnDestroy {
  layerService = inject(LayerService);
  mapContainerService = inject(MapContainerService);

  private readonly infoTextService = inject(InfoTextService);
  private readonly mapLayerService = inject(MapLayerService);
  private readonly baseMapService = inject(BasemapService);
  private readonly translocoService = inject(TranslocoService);
  private readonly notificationService = inject(NotificationService);
  private readonly popupService = inject(PopupService);
  private readonly cdref = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly layerApiService = inject(LayerApiService);
  private readonly dialogRef = inject<MatDialogRef<LayerComponent>>(MatDialogRef);
  private readonly fb = inject(FormBuilder);
  private readonly optionService = inject(OptionService);
  private readonly router = inject(Router);

  public selectedFilterId = 1;
  isScrollable = false;
  isLoading = signal(false);
  hasError = signal(false);

  layerForm!: FormGroup<LayerFormType>;

  // ViewModel computed signal that reacts to locale changes
  layerGroupViewModels = computed<LayerGroupViewModel[]>(() => {
    // Force recomputation when language changes
    this.optionService.localeChanged();

    const layerResponse = this.layerService.layerOverviewResponse();

    return layerResponse?.LayerGroup?.map(group => this.createLayerGroupViewModel(group)) ?? [];
  });

  constructor() {
    effect(() => {
      const layerOverviewResponse = this.layerService.layerOverviewResponse();
      if (layerOverviewResponse && this.layerForm) {
        this.updateFormFromLayerData();
        this.cdref.detectChanges();
      }
    });
  }

  ngOnInit() {
    this.hasError.set(false);

    this.initForm();

    if (this.layerService.layerOverviewResponse()) {
      return;
    }

    this.isLoading.set(true);

    this.layerApiService
      .getLayerOverview()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(layerOverview => {
          if (layerOverview.Status === InterfaceStatus.OK && layerOverview.LayerGroup) {
            this.sortLayerGroups(layerOverview.LayerGroup);
            this.deactivateAllLayers(layerOverview.LayerGroup);
          }
        })
      )
      .subscribe({
        next: layerOverview => {
          this.initializeLayerOverview(layerOverview);
          this.updateFormFromLayerData();
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
          this.hasError.set(true);
        }
      });

    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(event => event instanceof NavigationStart)
      )
      .subscribe((event: NavigationStart) => {
        if (event.url === "/portfolio-overview") {
          this.closeLayers();
        }
      });
  }
  ngOnDestroy() {
    this.layerService.isLayerPanelOpen.set(false);
  }

  protected closeLayers() {
    this.layerService.layerOverviewResponse.set(null);
    this.layerService.isLayerPanelOpen.set(false);

    this.popupService.closeActivePopup();
    this.mapLayerService.removeLayerByType(MapLayer.SelectedFeature);
    this.mapLayerService.removeLayerByType(MapLayer.LandUse);

    this.dialogRef.close();
  }

  protected minimizeLayers() {
    this.layerService.isLayerPanelOpen.set(false);
    this.dialogRef.close();
  }

  protected onScroll(event: Event) {
    const element = event.target as HTMLElement;
    if (!element) return;

    const isContentScrollable = this.isContentScrollable(element);
    this.isScrollable = isContentScrollable && element.scrollTop > 0;
    this.cdref.detectChanges();
  }

  protected trackByLayerGroup(index: number, control: FormGroup<LayerGroupForm>): number {
    return control.controls.id.value;
  }

  private isContentScrollable(element: HTMLElement): boolean {
    return element.scrollHeight > element.clientHeight;
  }

  // Initialize the form structure
  private initForm() {
    this.layerForm = this.fb.group<LayerFormType>({
      layerGroups: this.fb.array<FormGroup<LayerGroupForm>>([])
    });
  }

  get layerGroupsFormArray() {
    return this.layerForm.controls.layerGroups;
  }

  // Update the form with data from layerService
  private updateFormFromLayerData() {
    const layerGroups = this.layerService.layerGroups();
    if (!layerGroups?.length) {
      return;
    }

    // Clear and recreate the form array
    this.layerForm.setControl("layerGroups", this.fb.array(layerGroups.map(group => this.createLayerGroupControl(group))));

    // Set up form change listeners
    this.setupFormChangeListeners();
  }

  private createLayerGroupControl(group: LayerGroupInfo) {
    const showActivateAllOption = this.getSingleLayers(group).length >= 2;
    const hasActiveSingleLayers = this.hasActiveLayers(group);

    const activeFilter = this.getActiveFilter(group.Filters);
    const filteredLayers = this.getLayersByFilter(group.LayerInfos, activeFilter);

    return this.fb.group<LayerGroupForm>({
      id: this.fb.control(group.Id),
      isExpanded: this.fb.control(group.IsExpanded),
      layerInfos: this.fb.array(group.LayerInfos.map(info => this.createLayerInfoControl(info))),
      filters: this.fb.array(group.Filters.map(filter => this.createFilterControl(filter))),
      showActivateAllOption: this.fb.control(showActivateAllOption),
      hasActiveSingleLayers: this.fb.control(hasActiveSingleLayers),
      filteredLayerInfos: this.fb.control(filteredLayers)
    });
  }

  private hasActiveLayers(group: LayerGroupInfo): boolean {
    return group.LayerInfos.some(info => info.IsActivated);
  }

  private createLayerInfoControl(layerInfo: LayerInfo) {
    return this.fb.group<LayerInfoForm>({
      id: this.fb.control(layerInfo.Id),
      isActivated: this.fb.control(layerInfo.IsActivated),
      singleLayer: this.fb.control(layerInfo.SingleLayer),
      filterId: this.fb.control(layerInfo.FilterId),
      showInfoText: this.fb.control(layerInfo.ShowInfoText)
    });
  }

  private createFilterControl(filter: LayerGroupInfoFilter) {
    return this.fb.group<FilterForm>({
      id: this.fb.control(filter.Id),
      isActivated: this.fb.control(filter.IsActivated)
    });
  }

  // Setup listeners for form changes
  private setupFormChangeListeners() {
    for (let i = 0; i < this.layerGroupsFormArray.length; i++) {
      const group = this.layerGroupsFormArray.at(i);

      // Listen for hasActiveSingleLayers changes
      group.controls.hasActiveSingleLayers.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isActivated => {
        const layerGroup = this.layerService.layerGroups()[i];
        if (isActivated) {
          this.activateAllSingleLayers(layerGroup);
        } else {
          this.deactivateAllSingleLayers(layerGroup);
        }
      });

      // Activate or deactivate a layer
      const layerInfosArray = group.controls.layerInfos;
      for (let j = 0; j < layerInfosArray.length; j++) {
        const layerInfo = layerInfosArray.at(j);
        layerInfo.controls.isActivated.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isActivated => {
          const layerGroupData = this.layerService.layerGroups()[i];
          const layerInfoData = layerGroupData.LayerInfos[j];
          this.handleLayerActivationChange(layerInfoData, isActivated);

          const hasActiveSingleLayers = this.areAllSingleLayersActive(layerGroupData);
          group.controls.hasActiveSingleLayers.setValue(hasActiveSingleLayers, { emitEvent: false });
        });
      }

      // Filter changes
      const filtersArray = group.controls.filters;
      for (let k = 0; k < filtersArray.length; k++) {
        const filter = filtersArray.at(k);
        filter.controls.isActivated.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isActivated => {
          const layerGroupData = this.layerService.layerGroups()[i];
          const filterData = layerGroupData.Filters[k];
          this.handleFilterChange(layerGroupData, filterData, isActivated);
        });
      }
    }
  }

  // Handle layer activation/deactivation
  private handleLayerActivationChange(activeLayerInfo: LayerInfo, isActivated: boolean) {
    const currentResponse = this.layerService.layerOverviewResponse();

    const updatedResponse = cloneDeep(currentResponse);

    let updatedLayer: LayerInfo = null;

    for (const group of updatedResponse.LayerGroup) {
      for (const layerInfo of group.LayerInfos) {
        if (layerInfo.Id === activeLayerInfo.Id) {
          // Aktualisiere den Layer
          updatedLayer = layerInfo;
          updatedLayer.IsActivated = isActivated;

          // Wenn der Layer aktiviert wird und kein SingleLayer ist
          if (isActivated && !updatedLayer.SingleLayer) {
            // Deaktiviere andere nicht-SingleLayers in allen Gruppen
            this.deactivateOtherLayers(updatedResponse, activeLayerInfo.Id);
          }

          break;
        }
      }

      if (updatedLayer) break;
    }

    this.layerService.layerOverviewResponse.set(updatedResponse);
  }

  private deactivateOtherLayers(response: LayerOverviewResponse, activeLayerId: number): void {
    for (const group of response.LayerGroup) {
      for (const layer of group.LayerInfos) {
        if (layer.Id !== activeLayerId && layer.IsActivated && !layer.SingleLayer) {
          layer.IsActivated = false;
        }
      }
    }
  }

  // Handle filter changes
  private handleFilterChange(layerGroup: LayerGroupInfo, filter: LayerGroupInfoFilter, isActivated: boolean) {
    const currentResponse = this.layerService.layerOverviewResponse();
    const updatedResponse = cloneDeep(currentResponse);

    const updatedLayerGroup = updatedResponse.LayerGroup.find(g => g.Id === layerGroup.Id);
    if (!updatedLayerGroup) return;

    const updatedFilter = updatedLayerGroup.Filters.find(f => f.Id === filter.Id);
    if (!updatedFilter) return;

    if (isActivated) {
      this.applyFilter(updatedLayerGroup, updatedFilter);
    } else {
      updatedFilter.IsActivated = false;
      updatedLayerGroup.LayerInfos.forEach(layerInfo => {
        if (layerInfo.FilterId === filter.Id && layerInfo.IsActivated) {
          layerInfo.IsActivated = false;
        }
      });
    }

    this.layerService.layerOverviewResponse.set(updatedResponse);

    const filteredLayers = isActivated ? this.getLayersByFilter(updatedLayerGroup.LayerInfos, updatedFilter) : [];

    const groupForm = this.layerGroupsFormArray.controls.find(g => g.value.id === layerGroup.Id);
    if (groupForm) {
      groupForm.controls.filteredLayerInfos.setValue(filteredLayers);

      if (isActivated) {
        const hasActiveSingleLayersAfterFilter = this.areAllSingleLayersActive(updatedLayerGroup);
        groupForm.controls.hasActiveSingleLayers.setValue(hasActiveSingleLayersAfterFilter, { emitEvent: false });
      }
    }
  }

  // Apply a filter to a layer group
  private applyFilter(layerGroup: LayerGroupInfo, filter: LayerGroupInfoFilter) {
    // First deactivate all other filters
    layerGroup.Filters.forEach(f => {
      if (f.Id !== filter.Id) {
        f.IsActivated = false;
      }
    });

    filter.IsActivated = true;

    // isLayerActivated wird automatisch berechnet
    this.popupService.closeActivePopup();

    // Remove layer from deselected subgroup
    const active = layerGroup.LayerInfos.find(e => e.IsActivated);
    if (active) {
      active.IsActivated = false;
    }

    if (this.layerService.layerGroups().filter(e => e.LayerInfos.find(f => f.IsActivated)).length === 0) {
      this.layerService.setBaseLayer();
    }
  }

  // Activate all single layers in a group
  private activateAllSingleLayers(layerGroup: LayerGroupInfo) {
    const currentResponse = this.layerService.layerOverviewResponse();
    const updatedResponse = cloneDeep(currentResponse);

    // Find the layer group in the cloned response
    const updatedLayerGroup = updatedResponse.LayerGroup.find(g => g.Id === layerGroup.Id);
    if (!updatedLayerGroup) return;

    // Activate only SingleLayers in the cloned response
    const singleLayers = updatedLayerGroup.LayerInfos.filter(layer => layer.SingleLayer);

    singleLayers.forEach(layerInfo => {
      layerInfo.IsActivated = true;
    });

    // Set the updated response
    this.layerService.layerOverviewResponse.set(updatedResponse);
  }

  // Deactivate all single layers in a group
  private deactivateAllSingleLayers(layerGroup: LayerGroupInfo) {
    const currentResponse = this.layerService.layerOverviewResponse();
    const updatedResponse = cloneDeep(currentResponse);

    const updatedLayerGroup = updatedResponse.LayerGroup.find(g => g.Id === layerGroup.Id);
    if (!updatedLayerGroup) return;

    const singleLayers = updatedLayerGroup.LayerInfos.filter(layer => layer.SingleLayer);

    singleLayers.forEach(layerInfo => {
      layerInfo.IsActivated = false;
    });

    updatedLayerGroup.Filters.forEach(filter => {
      filter.IsActivated = false;
    });

    updatedLayerGroup.LayerInfos.forEach(layerInfo => {
      if (!layerInfo.SingleLayer && layerInfo.IsActivated) {
        layerInfo.IsActivated = false;
      }
    });

    this.layerService.layerOverviewResponse.set(updatedResponse);

    const groupForm = this.layerGroupsFormArray.controls.find(g => g.value.id === layerGroup.Id);
    if (groupForm) {
      groupForm.controls.filteredLayerInfos.setValue([]);
    }
  }

  private deactivateAllLayers(groups: LayerGroupInfo[]) {
    groups.forEach(e => {
      e.IsExpanded = false;
      e.LayerInfos.forEach(f => {
        f.ShowInfoText = false;
        f.IsActivated = false;
      });
    });
  }

  private sortLayerGroups(groups: LayerGroupInfo[]) {
    groups.sort((a, b) => (a.OrderId > b.OrderId ? 1 : -1));
    for (const e of groups) {
      e.LayerInfos.sort((a, b) => (a.OrderId > b.OrderId ? 1 : -1));
    }
  }

  private initializeLayerOverview(layerOverviewResponse: LayerOverviewResponse) {
    if (layerOverviewResponse.Status === InterfaceStatus.OK) {
      this.layerService.layerOverviewResponse.set(layerOverviewResponse);
      return;
    }

    this.handleInterfaceStatus(layerOverviewResponse.Status);
    this.layerService.layerOverviewResponse.set(new LayerOverviewResponse());
  }

  private handleInterfaceStatus(status: InterfaceStatus) {
    if (status === InterfaceStatus.ActivePackagePromoNotInCountry) {
      this.baseMapService.country.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(country => {
        this.notificationService.failure("Exception.Failure.Status." + status, {
          Country: this.translocoService.translate("Properties.Country." + country)
        });
      });
    } else {
      this.notificationService.failure("Exception.Failure.Status." + status);
    }
  }

  private areAllSingleLayersActive(layerGroup: LayerGroupInfo): boolean {
    const singleLayers = layerGroup.LayerInfos.filter(layer => layer.SingleLayer);
    const activeSingleLayers = singleLayers.filter(layer => layer.IsActivated);
    return singleLayers.length > 0 && singleLayers.length === activeSingleLayers.length;
  }

  private getSingleLayers(layerGroup: LayerGroupInfo): LayerInfo[] {
    return layerGroup.LayerInfos.filter(layer => layer.SingleLayer);
  }

  private getLayersByFilter(layers: LayerInfo[], filter?: LayerGroupInfoFilter): LayerInfo[] {
    if (!filter?.IsActivated) {
      return [];
    }
    return layers.filter(layer => layer.FilterId === filter.Id);
  }

  private getActiveFilter(filters: LayerGroupInfoFilter[]): LayerGroupInfoFilter | undefined {
    return filters.find(filter => filter.IsActivated);
  }

  private createLayerGroupViewModel(group: LayerGroupInfo): LayerGroupViewModel {
    return {
      id: group.Id,
      displayName: this.getLocalizedText(group.DisplayName),
      isExpanded: group.IsExpanded,
      layerInfos: group.LayerInfos.map(layerInfo => this.createLayerInfoViewModel(layerInfo)),
      filters: group.Filters.map(filter => this.createFilterViewModel(filter)),
      showActivateAllOption: this.getSingleLayers(group).length >= 2,
      hasActiveSingleLayers: this.hasActiveLayers(group),
      filteredLayerInfos: this.getFilteredLayerInfoViewModels(group),
      hasFilters: group.Filters.length > 0
    };
  }

  private createLayerInfoViewModel(layerInfo: LayerInfo): LayerInfoViewModel {
    return {
      id: layerInfo.Id,
      displayName: this.getLocalizedText(layerInfo.DisplayName),
      displayInfoText: this.getLocalizedText(layerInfo.DisplayInfoText),
      isActivated: layerInfo.IsActivated,
      singleLayer: layerInfo.SingleLayer,
      filterId: layerInfo.FilterId,
      showInfoText: layerInfo.ShowInfoText,
      availableCountries: layerInfo.AvailableCountries
    };
  }

  private createFilterViewModel(filter: LayerGroupInfoFilter): FilterViewModel {
    return {
      id: filter.Id,
      displayName: this.getLocalizedText(filter.DisplayName),
      isActivated: filter.IsActivated
    };
  }

  private getFilteredLayerInfoViewModels(group: LayerGroupInfo): LayerInfoViewModel[] {
    const activeFilter = this.getActiveFilter(group.Filters);
    const filteredLayers = this.getLayersByFilter(group.LayerInfos, activeFilter);
    return filteredLayers.map(layerInfo => this.createLayerInfoViewModel(layerInfo));
  }

  private getLocalizedText(infoText: InfoText[]): string {
    if (!infoText) return "";
    const localizedText = this.infoTextService.getInfoTextByLocale(infoText);
    return localizedText?.[0]?.Text || "";
  }

  // Method to toggle layer activation - for accessibility
  private toggleLayerActivation(layerInfoControl: FormGroup<LayerInfoForm>): void {
    const currentValue = layerInfoControl.controls.isActivated.value;
    layerInfoControl.controls.isActivated.setValue(!currentValue);
  }

  /*
   ** The following fns aren't used atm
   ** @Philipp pls delete them if you don't need them anymore
   */
  // Method to handle keyboard events for accessibility
  private onLayerKeydown(event: KeyboardEvent, layerInfoControl: FormGroup<LayerInfoForm>): void {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.toggleLayerActivation(layerInfoControl);
    }
  }

  private trackByLayerInfo(index: number, control: FormGroup<LayerInfoForm>): number {
    return control.controls.id.value;
  }

  private trackByFilter(index: number, control: FormGroup<FilterForm>): number {
    return control.controls.id.value;
  }

  private trackByFilteredLayerInfo(index: number, layerInfo: LayerInfoViewModel): number {
    return layerInfo.id;
  }
}
